export type TimelineNode = {
  time: string;
  title: string;
  detail: string;
  tag: string;
};

export type EvidenceItem = {
  label: string;
  type: string;
  desc: string;
  level: string;
};

export type RelationNode = {
  id: string;
  name: string;
  role: string;
  kind: "core" | "ally" | "opponent" | "neutral";
};

export type RelationLink = {
  from: string;
  to: string;
  label: string;
  tone: "positive" | "conflict" | "neutral";
};

export type GossipEvent = {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  categorySlug: string;
  cover: string;
  date: string;
  heat: number;
  comments: number;
  readMin: number;
  summary: string;
  background: string;
  cause: string;
  outcome: string;
  tags: string[];
  timeline: TimelineNode[];
  evidence: EvidenceItem[];
  relations: RelationNode[];
  links: RelationLink[];
};

export type Category = {
  slug: string;
  name: string;
  desc: string;
  icon: string;
  accent: string;
};

export const categories: Category[] = [
  { slug: "star-love", name: "明星恋情", desc: "同框、互关、同居传闻的完整时间线梳理", icon: "ri-heart-3-line", accent: "primary" },
  { slug: "film-crew", name: "影视剧组", desc: "换角、抢番、片场关系的幕后复盘", icon: "ri-film-line", accent: "accent" },
  { slug: "variety", name: "综艺幕后", desc: "剪辑、镜头分配与节目组互动的解码", icon: "ri-live-line", accent: "secondary" },
  { slug: "biz-dispute", name: "商务纠纷", desc: "解约、合约、代言归属的前因后果", icon: "ri-briefcase-line", accent: "primary" },
  { slug: "scandal", name: "塌房实锤", desc: "录音、截图、时间线的证据级整理", icon: "ri-alarm-warning-line", accent: "accent" },
  { slug: "circle-feud", name: "圈内恩怨", desc: "经纪人、团队与明星之间的隔空交锋", icon: "ri-sword-line", accent: "secondary" },
];

export const events: GossipEvent[] = [
  {
    id: "shen-lu-same-frame",
    title: "沈知遥 × 陆行舟 深夜同框事件",
    subtitle: "从片场偶遇拍到同日返程，三个月时间线完整还原",
    category: "明星恋情",
    categorySlug: "star-love",
    cover: "https://readdy.ai/api/search-image?query=Cinematic%20moody%20editorial%20photograph%20of%20two%20blurred%20silhouettes%20walking%20at%20night%20under%20warm%20amber%20street%20lamps%20wet%20pavement%20reflections%20deep%20rust%20and%20gold%20tones%20shallow%20depth%20of%20field%20documentary%20aesthetic%20film%20grain%20high%20detail&width=800&height=600&seq=gossip-ev1-2026&orientation=landscape",
    date: "2026-09-12",
    heat: 9860,
    comments: 1423,
    readMin: 8,
    summary: "两人先在剧组被拍到共用休息车，随后又被目击同日出现在机场贵宾通道。本站按时间顺序整理全部公开画面与行程线索，还原三个月里的每一次「巧合」。",
    background: "沈知遥与陆行舟因合作剧集《雾中来信》首次搭档，开机前双方团队均表示「只是普通同事关系」。剧集杀青后，两人社交账号的互动频次明显上升，引发外界关注。",
    cause: "事件的起点并非某一次偷拍，而是粉丝整理的「行程重叠表」。该表格把两人近三个月的公开行程按日期排列，发现其中有 11 天出现在同一城市，远高于随机概率。",
    outcome: "截至发稿，双方团队均未正面回应。业内普遍认为，此类沉默通常意味着暂不打算公开，也不打算否认，属于典型的「冷处理」阶段。",
    tags: ["同框实拍", "行程重叠", "片场线索", "时间线复盘"],
    timeline: [
      { time: "06-18", title: "剧组开机，首次同框", detail: "《雾中来信》举行开机仪式，两人分列主演阵容前排，现场互动被指「客气但有距离感」。", tag: "起点" },
      { time: "07-05", title: "共用休息车被拍到", detail: "有探班网友拍到两人先后进入同一辆保姆车，停留约四十分钟后一同下车，走向不同的化妆间。", tag: "关键画面" },
      { time: "07-29", title: "社交账号首次互动", detail: "陆行舟为沈知遥的剧集宣传动态点赞，这是两人账号互动的第一次记录。", tag: "细节" },
      { time: "08-16", title: "同日返程引发讨论", detail: "两人被目击出现在同一航班的贵宾通道，行程表显示目的地一致，但落地后分别由不同车辆接走。", tag: "关键画面" },
      { time: "09-03", title: "粉丝整理行程重叠表", detail: "一份按日期排列的行程对照表在讨论区流传，显示三个月内有 11 天出现在同一城市。", tag: "发酵" },
      { time: "09-12", title: "双方团队均未回应", detail: "截至发稿，双方工作室均未就此事发表任何声明，事件热度仍在上升。", tag: "现状" },
    ],
    evidence: [
      { label: "片场共用休息车画面", type: "现场画面", desc: "探班网友拍摄，可辨认车辆涂装与两人身形，但画面较远，无法确认车内细节。", level: "旁证" },
      { label: "行程重叠对照表", type: "时间线索", desc: "由讨论区网友整理，覆盖近三个月公开行程，其中 11 天城市重合。", level: "间接证据" },
      { label: "社交账号点赞记录", type: "线上痕迹", desc: "可确认的互动仅一次点赞，后续无评论与转发。", level: "弱相关" },
      { label: "机场贵宾通道目击", type: "现场画面", desc: "有目击者描述两人同行，但无清晰照片流出。", level: "旁证" },
    ],
    relations: [
      { id: "core", name: "沈知遥 × 陆行舟", role: "事件核心双方", kind: "core" },
      { id: "agent", name: "双方经纪团队", role: "统一口径沉默", kind: "neutral" },
      { id: "crew", name: "《雾中来信》剧组", role: "合作契机来源", kind: "ally" },
      { id: "fans", name: "行程整理网友", role: "线索提供方", kind: "ally" },
      { id: "media", name: "娱乐媒体", role: "持续跟进报道", kind: "neutral" },
    ],
    links: [
      { from: "core", to: "crew", label: "合作拍戏", tone: "positive" },
      { from: "core", to: "agent", label: "口径统一", tone: "neutral" },
      { from: "fans", to: "core", label: "整理行程", tone: "positive" },
      { from: "media", to: "core", label: "跟进求证", tone: "neutral" },
    ],
  },
  {
    id: "long-night-recast",
    title: "《长夜未明》剧组换角风波",
    subtitle: "开播前两周临时换人，官方与片场说法为何对不上",
    category: "影视剧组",
    categorySlug: "film-crew",
    cover: "https://readdy.ai/api/search-image?query=Cinematic%20moody%20editorial%20photograph%20of%20an%20empty%20film%20set%20with%20director%20chair%20and%20vintage%20spotlight%20warm%20amber%20light%20deep%20rust%20tones%20dust%20in%20the%20air%20shallow%20depth%20of%20field%20documentary%20aesthetic%20film%20grain&width=800&height=600&seq=gossip-ev2-2026&orientation=landscape",
    date: "2026-09-09",
    heat: 8740,
    comments: 986,
    readMin: 7,
    summary: "原定女主在开播前两周被替换，官方称「档期冲突」，但片场工作人员透露的版本完全不同。我们把两次官方声明与三份片场爆料放在同一条时间线上对比。",
    background: "《长夜未明》是今年的重点剧集项目，原定女主白棠已完成大部分定妆与宣传物料的拍摄。剧组在开播前两周突然宣布换角，由演员孟琳接替。",
    cause: "官方首次声明称「因档期冲突，双方协商后友好解约」。但随后有片场工作人员爆料，称换角与一场重头戏的拍摄分歧有关，双方说法出现明显矛盾。",
    outcome: "剧组在第二次声明中改口称「系正常创作调整」，但未解释与首次声明的差异。白棠方面发布了简短的感谢声明，未提及具体原因。",
    tags: ["换角", "官方声明", "片场爆料", "说法矛盾"],
    timeline: [
      { time: "08-20", title: "定妆物料完成拍摄", detail: "白棠完成最后一批宣传定妆，物料已进入后期排版阶段。", tag: "起点" },
      { time: "08-26", title: "重头戏拍摄出现分歧", detail: "据片场工作人员描述，双方在一场重头戏的表演处理上存在明显分歧。", tag: "矛盾" },
      { time: "09-02", title: "官方首次发布换角声明", detail: "剧组称「因档期冲突协商解约」，措辞简短，未提及具体细节。", tag: "官方" },
      { time: "09-05", title: "片场爆料流传", detail: "有剧组工作人员在讨论区发帖，描述的换角原因与官方声明不一致。", tag: "发酵" },
      { time: "09-09", title: "官方第二次声明改口", detail: "剧组改称「系正常创作调整」，但未解释与此前声明的差异。", tag: "现状" },
    ],
    evidence: [
      { label: "首次官方换角声明", type: "官方文书", desc: "明确写明「因档期冲突」，是判断说法的基准文件。", level: "直接证据" },
      { label: "片场工作人员爆料", type: "匿名描述", desc: "描述了分歧的具体场景，但爆料者身份无法核实。", level: "旁证" },
      { label: "已拍摄的定妆物料", type: "现场物料", desc: "证明原定演员已进入深度参与阶段，与「临时档期冲突」说法存在张力。", level: "间接证据" },
    ],
    relations: [
      { id: "core", name: "《长夜未明》剧组", role: "事件核心方", kind: "core" },
      { id: "baitang", name: "白棠", role: "原定女主", kind: "opponent" },
      { id: "menglin", name: "孟琳", role: "接替演员", kind: "neutral" },
      { id: "insider", name: "片场工作人员", role: "爆料来源", kind: "ally" },
    ],
    links: [
      { from: "core", to: "baitang", label: "解约换角", tone: "conflict" },
      { from: "core", to: "menglin", label: "接替出演", tone: "neutral" },
      { from: "insider", to: "core", label: "披露内情", tone: "conflict" },
    ],
  },
  {
    id: "variety-edit-war",
    title: "综艺《即刻出发》剪辑争议",
    subtitle: "同一段素材两个版本，镜头分配到底发生了什么",
    category: "综艺幕后",
    categorySlug: "variety",
    cover: "https://readdy.ai/api/search-image?query=Cinematic%20moody%20editorial%20photograph%20of%20a%20video%20editing%20workstation%20with%20multiple%20monitors%20showing%20timelines%20dim%20room%20warm%20amber%20desk%20lamp%20deep%20rust%20highlights%20shallow%20depth%20of%20field%20documentary%20aesthetic%20film%20grain&width=800&height=600&seq=gossip-ev3-2026&orientation=landscape",
    date: "2026-09-06",
    heat: 7320,
    comments: 764,
    readMin: 6,
    summary: "节目播出后，嘉宾的镜头时长引发讨论。有观众统计了正片与花絮中同一段对话的两个版本，发现关键语句的取舍存在明显差异。",
    background: "《即刻出发》是一档户外真人秀，本季请来四位常驻嘉宾。播出第三期后，其中一位嘉宾的粉丝指出正片中其镜头时长明显少于宣传物料。",
    cause: "争议的触发点是一段公开的花絮片段——花絮中嘉宾有一段完整的观点表达，而正片里只保留了其中一句，且被剪辑到完全不同的语境中。",
    outcome: "节目组未就剪辑问题单独回应，仅表示「正片以节目整体节奏为准」。讨论区随后出现更多逐帧对比，事件热度维持在高位。",
    tags: ["剪辑对比", "镜头分配", "逐帧分析", "节目回应"],
    timeline: [
      { time: "08-30", title: "第三期正片播出", detail: "正片中某位嘉宾的镜头时长约 6 分钟，明显低于另外三位。", tag: "起点" },
      { time: "09-01", title: "观众开始统计时长", detail: "有观众逐段统计了四位嘉宾的正片出镜时长，并制作了对照图表。", tag: "发酵" },
      { time: "09-03", title: "花絮片段被翻出", detail: "一段花絮中嘉宾的完整表达，与正片中的呈现语境存在明显差异。", tag: "关键素材" },
      { time: "09-06", title: "节目组统一回应", detail: "节目组表示「正片以整体节奏为准」，未针对具体剪辑作出解释。", tag: "现状" },
    ],
    evidence: [
      { label: "节目时长统计表", type: "数据整理", desc: "观众逐段统计的正片出镜时长，是讨论的量化基础。", level: "直接证据" },
      { label: "花絮与正片对比", type: "素材对比", desc: "同一段对话的两个版本，明显可辨的取舍差异。", level: "直接证据" },
      { label: "节目组统一回应", type: "官方说明", desc: "未具体解释剪辑逻辑，属于概括性说明。", level: "旁证" },
    ],
    relations: [
      { id: "core", name: "《即刻出发》节目组", role: "事件核心方", kind: "core" },
      { id: "guest", name: "争议嘉宾", role: "镜头偏少一方", kind: "opponent" },
      { id: "viewers", name: "统计观众", role: "数据分析来源", kind: "ally" },
      { id: "other", name: "其余三位常驻", role: "镜头相对充足", kind: "neutral" },
    ],
    links: [
      { from: "core", to: "guest", label: "镜头取舍争议", tone: "conflict" },
      { from: "viewers", to: "core", label: "逐帧对比", tone: "conflict" },
      { from: "core", to: "other", label: "常规呈现", tone: "neutral" },
    ],
  },
  {
    id: "suwan-contract",
    title: "苏晚工作室解约纠纷",
    subtitle: "从合约到期到账号归属，一场持续半年的拉锯",
    category: "商务纠纷",
    categorySlug: "biz-dispute",
    cover: "https://readdy.ai/api/search-image?query=Cinematic%20moody%20editorial%20photograph%20of%20a%20signed%20contract%20document%20and%20fountain%20pen%20on%20dark%20walnut%20desk%20with%20reading%20glasses%20warm%20amber%20lamp%20glow%20deep%20rust%20tones%20shallow%20depth%20of%20field%20documentary%20aesthetic%20film%20grain&width=800&height=600&seq=gossip-ev4-2026&orientation=landscape",
    date: "2026-09-02",
    heat: 6480,
    comments: 521,
    readMin: 7,
    summary: "围绕合约到期后的账号归属与商务尾款，双方在半年内先后发布四份声明。我们把每份声明的时间与措辞变化整理成一条清晰的博弈线。",
    background: "苏晚与原工作室的合作合约在今年三月到期。双方在续约谈判中未能达成一致，随后就社交账号归属与既有商务的尾款分配产生分歧。",
    cause: "争议的核心是账号归属。原工作室主张账号由公司运营团队打造，应归属公司；苏晚方则认为账号内容与个人形象强绑定，应归个人所有。",
    outcome: "目前账号处于停更状态，双方各自保留法律追究的权利。商务尾款的结算仍在协商中。",
    tags: ["合约到期", "账号归属", "四份声明", "拉锯战"],
    timeline: [
      { time: "03-10", title: "合约到期未续约", detail: "双方在续约条款上未能达成一致，合作于合约到期后自然终止。", tag: "起点" },
      { time: "04-22", title: "工作室发布首份声明", detail: "工作室主张账号归属公司，并要求结算既有商务的分成。", tag: "官方" },
      { time: "05-18", title: "苏晚方回函", detail: "苏晚方表示账号应归个人所有，对分成比例提出异议。", tag: "回应" },
      { time: "08-07", title: "账号进入停更", detail: "双方未能达成一致，社交账号停止更新，商业合作同步暂停。", tag: "僵持" },
      { time: "09-02", title: "各自保留追究权利", detail: "截至目前，双方均未提起诉讼，但都表示保留法律手段。", tag: "现状" },
    ],
    evidence: [
      { label: "四份公开声明", type: "官方文书", desc: "完整记录双方措辞变化，是复盘博弈过程的核心材料。", level: "直接证据" },
      { label: "账号历史运营记录", type: "线上痕迹", desc: "内容兼具公司策划与个人风格，是归属争议的关键。", level: "间接证据" },
      { label: "商务合作清单", type: "合同材料", desc: "涉及多笔未结算商务，具体金额未公开。", level: "间接证据" },
    ],
    relations: [
      { id: "core", name: "苏晚", role: "艺人方", kind: "core" },
      { id: "studio", name: "原工作室", role: "公司方", kind: "opponent" },
      { id: "brands", name: "合作品牌", role: "尾款相关方", kind: "neutral" },
      { id: "lawyer", name: "双方法务", role: "协商主导", kind: "neutral" },
    ],
    links: [
      { from: "core", to: "studio", label: "账号归属争议", tone: "conflict" },
      { from: "studio", to: "brands", label: "尾款结算", tone: "neutral" },
      { from: "lawyer", to: "core", label: "法律协商", tone: "neutral" },
    ],
  },
  {
    id: "boyband-tape",
    title: "男团主唱塌房录音流出",
    subtitle: "一段 47 秒音频的完整溯源与可信度评估",
    category: "塌房实锤",
    categorySlug: "scandal",
    cover: "https://readdy.ai/api/search-image?query=Cinematic%20moody%20editorial%20photograph%20of%20a%20vintage%20audio%20reel%20to%20reel%20tape%20recorder%20with%20glowing%20VU%20meters%20on%20dark%20wooden%20desk%20warm%20amber%20light%20deep%20rust%20tones%20shallow%20depth%20of%20field%20documentary%20aesthetic%20film%20grain&width=800&height=600&seq=gossip-ev5-2026&orientation=landscape",
    date: "2026-08-28",
    heat: 9120,
    comments: 1876,
    readMin: 6,
    summary: "一段 47 秒的音频在两个小时内被转发上万次。本页不站队，只做溯源：音频最早出现在哪里、被怎样剪辑、哪些部分可以核实、哪些部分无法确认。",
    background: "某男团主唱近日陷入舆论风波，起因是一段在社交平台流传的 47 秒音频。音频中疑似出现该主唱的声音，内容引发争议。",
    cause: "音频最早出现在一个匿名讨论帖中，发布者未提供来源。随后被大量搬运与二次剪辑，出现了多个版本，部分版本的时长与内容并不一致。",
    outcome: "截至目前，音频的原始来源仍未确认，多版本之间的差异使完整还原变得困难。本站建议以「待核实」态度看待。",
    tags: ["音频溯源", "多版本", "待核实", "证据评估"],
    timeline: [
      { time: "08-24", title: "音频首次出现在讨论帖", detail: "一段 47 秒音频出现在匿名讨论帖中，发布者未提供来源与原始文件。", tag: "起点" },
      { time: "08-25", title: "出现多个二次剪辑版本", detail: "音频被大量搬运，部分版本时长与内容发生变化，出现明显拼接痕迹。", tag: "发酵" },
      { time: "08-27", title: "粉丝与网友各自比对", detail: "围绕音频的可信度，双方分别尝试从音色与语速角度进行比对，结论不一。", tag: "争论" },
      { time: "08-28", title: "来源仍未被确认", detail: "截至目前，尚无任何一方能提供音频的原始来源或完整上下文。", tag: "现状" },
    ],
    evidence: [
      { label: "47 秒原始音频", type: "音频材料", desc: "最早出现的版本，来源不明，无法确认录制时间与场景。", level: "待核实" },
      { label: "二次剪辑版本", type: "修改材料", desc: "存在明显拼接痕迹，与原始版本内容不一致，不宜作为依据。", level: "不可采信" },
      { label: "音色比对讨论", type: "主观判断", desc: "双方各自给出的比对结论相反，缺乏技术鉴定支持。", level: "弱相关" },
    ],
    relations: [
      { id: "core", name: "男团主唱", role: "被讨论对象", kind: "core" },
      { id: "leaker", name: "匿名发布者", role: "音频来源", kind: "opponent" },
      { id: "agency", name: "所属公司", role: "未正面回应", kind: "neutral" },
      { id: "fandom", name: "粉丝群体", role: "组织比对", kind: "ally" },
    ],
    links: [
      { from: "leaker", to: "core", label: "发布音频", tone: "conflict" },
      { from: "fandom", to: "core", label: "声援比对", tone: "positive" },
      { from: "agency", to: "core", label: "保持沉默", tone: "neutral" },
    ],
  },
  {
    id: "agents-feud",
    title: "两大经纪人隔空互撕",
    subtitle: "两条动态、三次删帖，一场没有点名的交锋",
    category: "圈内恩怨",
    categorySlug: "circle-feud",
    cover: "https://readdy.ai/api/search-image?query=Cinematic%20moody%20editorial%20photograph%20of%20two%20chess%20king%20pieces%20facing%20off%20on%20dark%20textured%20board%20with%20dramatic%20side%20lighting%20warm%20amber%20and%20deep%20rust%20tones%20shallow%20depth%20of%20field%20documentary%20aesthetic%20film%20grain&width=800&height=600&seq=gossip-ev6-2026&orientation=landscape",
    date: "2026-08-21",
    heat: 5940,
    comments: 438,
    readMin: 5,
    summary: "两位资深经纪人在同一晚先后发布动态，均未点名却指向明显。我们把发布、删除、再发布的时间点连起来，看看这场交锋的回合制节奏。",
    background: "两位在业内颇有资历的经纪人，此前因旗下艺人的资源竞争已多次被传不合。本次交锋的导火索是一条关于「资源分配」的公开动态。",
    cause: "第一晚，其中一方发布了一条关于「行业风气」的动态，措辞克制但指向明确。数小时后，另一方发布了一条内容呼应但对象反转的动态。",
    outcome: "两条动态均在发布数小时后被删除。截至目前，双方均未再就此事发声，事件逐渐平息。",
    tags: ["隔空交锋", "删帖", "资源竞争", "回合制"],
    timeline: [
      { time: "08-18", title: "首条动态发布", detail: "一方发布关于「行业风气」的动态，未点名，但被普遍解读为指向另一方。", tag: "起点" },
      { time: "08-18", title: "内容被删除", detail: "动态在发布约三小时后被删除，未作任何说明。", tag: "转折" },
      { time: "08-19", title: "另一方次日回应", detail: "另一方发布内容呼应的动态，措辞更为直接，同样未点名。", tag: "回应" },
      { time: "08-21", title: "双方均停止发声", detail: "回应动态也被删除，此后双方未再公开提及此事。", tag: "现状" },
    ],
    evidence: [
      { label: "两条动态截图", type: "线上痕迹", desc: "发布与删除的时间点可交叉验证，是复盘节奏的核心材料。", level: "直接证据" },
      { label: "此前的资源竞争记录", type: "背景材料", desc: "为理解双方矛盾提供背景，但无直接关联证据。", level: "旁证" },
    ],
    relations: [
      { id: "core", name: "经纪人甲", role: "先发文方", kind: "core" },
      { id: "b", name: "经纪人乙", role: "回应方", kind: "opponent" },
      { id: "artists", name: "双方旗下艺人", role: "资源竞争关联", kind: "neutral" },
      { id: "industry", name: "业内同行", role: "观望态度", kind: "neutral" },
    ],
    links: [
      { from: "core", to: "b", label: "隔空交锋", tone: "conflict" },
      { from: "artists", to: "core", label: "资源重叠", tone: "conflict" },
      { from: "industry", to: "b", label: "保持观望", tone: "neutral" },
    ],
  },
];

export const hotRanking = [
  { rank: 1, id: "shen-lu-same-frame", title: "沈知遥 × 陆行舟 深夜同框事件", heat: 9860 },
  { rank: 2, id: "boyband-tape", title: "男团主唱塌房录音流出", heat: 9120 },
  { rank: 3, id: "long-night-recast", title: "《长夜未明》剧组换角风波", heat: 8740 },
  { rank: 4, id: "variety-edit-war", title: "综艺《即刻出发》剪辑争议", heat: 7320 },
  { rank: 5, id: "suwan-contract", title: "苏晚工作室解约纠纷", heat: 6480 },
];

export const faqs = [
  {
    q: "「时间线复盘」和普通的八卦帖有什么区别？",
    a: "普通的八卦帖往往只呈现某个片段，容易断章取义。本站的复盘会把所有可核实的节点按时间排列，标注每个节点的来源与性质，让「前因」和「后果」之间的关系一目了然，而不是只给你一个结论。",
  },
  {
    q: "你们如何判断一条线索的可信度？",
    a: "我们把材料分为直接证据、间接证据、旁证与待核实四档。官方文书、可交叉验证的时间记录属于直接证据；匿名爆料、模糊画面属于旁证。凡是无法溯源的材料，我们会明确标注「待核实」，不会当成事实使用。",
  },
  {
    q: "关系图是怎么做出来的？",
    a: "关系图只呈现有公开信息支撑的关系，例如同剧组、同公司、公开的合作或公开的交锋。我们不会凭猜测连线，每条连线都会对应正文中的某个时间节点。",
  },
  {
    q: "发现内容有误怎么办？",
    a: "欢迎通过页面底部的邮箱联系我们，并附上可核实的依据。我们会优先复核被指出问题的节点，确认有误后会第一时间更正并在页面标注修订记录。",
  },
  {
    q: "为什么有些内容没有给出结论？",
    a: "因为在信息不足的时候，给结论本身就是一种误导。我们更愿意把材料摊开、把不确定的地方标出来，让你自己判断，这也是「吃瓜要吃透」的本意。",
  },
];

export const siteStats = [
  { value: "1,280+", label: "已复盘事件" },
  { value: "9,600+", label: "整理时间节点" },
  { value: "3,400+", label: "关系连线" },
  { value: "98%", label: "来源可追溯率" },
];

export const aboutPillars = [
  {
    icon: "ri-compass-3-line",
    title: "网站定位",
    desc: "我们不做最快的那一个，只做把话说清楚的那一个。相比抢首发，我们更在意一条线索能不能被复核、一个结论有没有时间线支撑。",
  },
  {
    icon: "ri-seedling-line",
    title: "发展理念",
    desc: "娱乐信息往往碎片而情绪化。我们希望通过结构化整理，让读者在一篇内容里看懂完整脉络，而不是被零散截图带着走。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "内容原则",
    desc: "只整理可核实的公开信息，标注每条材料可信度；存在争议的部分并列呈现双方说法，不替读者下判断。",
  },
  {
    icon: "ri-team-line",
    title: "协作方式",
    desc: "线索来自读者投稿与公开信息，编辑负责交叉核对与时间线编排。任何被指出有误的节点，都会优先复核并公开更正。",
  },
];

export const testimonials = [
  {
    name: "小满",
    role: "普通读者",
    quote:
      "以前刷到瓜都是一头雾水，只看到几张截图。这里把时间线一列，前因后果一下就串起来了，省了很多翻来翻去的时间。",
  },
  {
    name: "阿荔",
    role: "内容爱好者",
    quote:
      "最喜欢的是它会标清楚哪些是实锤、哪些只是待核实。很多地方直接把猜测当结论写，看久了会很累。",
  },
  {
    name: "老周",
    role: "饭圈观察者",
    quote:
      "关系图那块挺实用，谁和谁是一条线一目了然。而且每条连线都能在正文里找到对应的节点，不是随便画的。",
  },
  {
    name: "南栀",
    role: "在读学生",
    quote:
      "回复很快。我指出过一处时间写错了，第二天就改了，还在页面标了修订记录，这点挺让人放心的。",
  },
];

export const milestones = [
  { year: "2023", title: "深扒档案室上线", desc: "确立以时间线为核心的内容结构，发布首批事件复盘。" },
  { year: "2024", title: "加入关系图解码", desc: "把人物关系可视化，并保证每条连线对应正文节点。" },
  { year: "2025", title: "建立可信度分级", desc: "对全部材料实行四档可信度标注，无法溯源的一律标为待核实。" },
  { year: "2026", title: "开放修订记录", desc: "上线读者纠错通道，所有更正内容公开记录修订时间。" },
];